'use client';

/**
 * ActivityTimeline — Phase A9 standardised activity / audit feed.
 *
 * Lifts the pattern used inside Phase 5 `incident-detail/activity-
 * timeline.tsx` into a reusable design-system primitive. Renders a
 * vertical timeline of activity events (system events + user comments)
 * with filter chips, lazy-load older entries, and consistent icons.
 *
 * Spec: `UX_PRODUCTION_ROADMAP.md` §A9 (Activity Timeline).
 *
 * Anatomy:
 *   ┌── Activity ──────────────────────────────────┐
 *   │ Filter: ●All  ○Comments  ○System events     │
 *   │                                              │
 *   │ ⏺ Created — System · 2h ago                 │
 *   │ │  Incident logged by Fiona (RN)            │
 *   │ ⏺ Comment — Helen · 1h ago                  │
 *   │ │  "Following up with family."              │
 *   │ ⏺ Status updated — System · 30m ago         │
 *   │ │  In Progress → Resolved                   │
 *   │                                              │
 *   │ [Load older …]                               │
 *   └──────────────────────────────────────────────┘
 *
 * Consumer owns:
 *   - The events array (typically merged from `audit_logs` + comments)
 *   - Lazy-load handler (`onLoadOlder`)
 *   - Filter state (default 'all' — set + handle via `filter` / `onFilterChange`)
 */

import React from 'react';
import { Activity, MessageSquare, Settings } from 'lucide-react';
import { cn } from '../../lib/utils';

export type ActivityTimelineFilter = 'all' | 'comments' | 'system';

export interface ActivityTimelineEvent {
  /** Stable id (React key). */
  id: string;
  /** When the event happened (ISO 8601). */
  timestamp: string;
  /** Pre-formatted display time (e.g. "2h ago", "27 May 14:32"). */
  timeLabel: string;
  /** Event kind — drives icon + filter. */
  kind: 'comment' | 'system';
  /** Headline label (e.g. "Created", "Comment", "Status updated"). */
  label: string;
  /** Who triggered the event (display name or "System"). */
  actor?: string;
  /**
   * Body content — for system events this is typically a "from → to"
   * diff line; for comments it's the comment text. Accepts JSX so the
   * consumer can render mentions, diffs, links, etc.
   */
  body?: React.ReactNode;
}

export interface ActivityTimelineProps {
  /** Events in display order (newest first is conventional, but not enforced). */
  events: ReadonlyArray<ActivityTimelineEvent>;
  /** Current filter — defaults to 'all'. */
  filter?: ActivityTimelineFilter;
  /** Filter change handler. Omit to render a static "all" timeline. */
  onFilterChange?: (filter: ActivityTimelineFilter) => void;
  /** Optional callback rendered as "Load older" button below the feed. */
  onLoadOlder?: () => void;
  /** Loading flag for an in-flight load-older. */
  loadingOlder?: boolean;
  /** Empty-state message — defaults to "No activity yet." */
  emptyMessage?: React.ReactNode;
  className?: string;
}

const FILTERS: ReadonlyArray<{ id: ActivityTimelineFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'comments', label: 'Comments' },
  { id: 'system', label: 'System events' },
];

export function ActivityTimeline({
  events,
  filter = 'all',
  onFilterChange,
  onLoadOlder,
  loadingOlder,
  emptyMessage = 'No activity yet.',
  className,
}: ActivityTimelineProps) {
  const visible = events.filter((event) => {
    if (filter === 'all') return true;
    if (filter === 'comments') return event.kind === 'comment';
    return event.kind === 'system';
  });

  return (
    <section
      aria-label="Activity timeline"
      className={cn('rounded-2xl border border-slate-200 bg-white p-4 shadow-sm', className)}
      data-activity-timeline
    >
      <header className="mb-3 flex min-w-0 flex-wrap items-start gap-x-4 gap-y-3">
        <h2 className="flex min-w-0 flex-[1_1_16rem] break-normal [overflow-wrap:break-word] items-center gap-1.5 text-eyebrow text-slate-500">
          <Activity className="h-3.5 w-3.5" aria-hidden="true" />
          Activity
        </h2>
        {onFilterChange && (
          <div
            role="group"
            aria-label="Activity filter"
            className="ml-auto flex min-w-0 max-w-full flex-[0_1_auto] flex-wrap items-center justify-start gap-2"
          >
            {FILTERS.map((f) => {
              const isActive = filter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => onFilterChange(f.id)}
                  className={cn(
                    'rounded-md px-2 py-0.5 text-[10.5px] font-medium transition',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible',
                    isActive
                      ? 'bg-statura-green-accessible/15 text-statura-green-accessible'
                      : 'text-slate-500 hover:bg-slate-50 hover:text-statura-ink',
                  )}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        )}
      </header>

      {visible.length === 0 ? (
        <p className="py-2 text-[11.5px] text-slate-500">{emptyMessage}</p>
      ) : (
        <ol role="list" className="space-y-2.5">
          {visible.map((event) => {
            const Icon = event.kind === 'comment' ? MessageSquare : Settings;
            return (
              <li key={event.id} className="flex items-start gap-2.5">
                <span
                  className={cn(
                    'mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                    event.kind === 'comment'
                      ? 'bg-statura-green-accessible/10 text-statura-green-accessible'
                      : 'bg-slate-100 text-slate-500',
                  )}
                  aria-hidden="true"
                >
                  <Icon className="h-3 w-3" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-baseline gap-1 text-[11.5px]">
                    <span className="font-semibold text-statura-ink">{event.label}</span>
                    {event.actor && <span className="text-slate-500">· {event.actor}</span>}
                    <time
                      dateTime={event.timestamp}
                      className="font-mono text-caption text-slate-400"
                    >
                      · {event.timeLabel}
                    </time>
                  </p>
                  {event.body && (
                    <div
                      className={cn(
                        'mt-1 text-[11.5px] leading-snug',
                        event.kind === 'system' ? 'italic text-slate-500' : 'text-text-body',
                      )}
                    >
                      {event.body}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {onLoadOlder && (
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={onLoadOlder}
            disabled={loadingOlder}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[10.5px] font-medium text-slate-600 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loadingOlder ? 'Loading…' : 'Load older'}
          </button>
        </div>
      )}
    </section>
  );
}
